// tslint:disable: cyclomatic-complexity
import React, { useState, useRef, useContext, useMemo } from 'react';
import { Route, withRouter } from 'react-router';
import { withStyles } from '@material-ui/core';
import classNames from 'classnames';
import { ActionBar, BreadcrumbBar, NavigationTabs } from '@markinson/uicomponents-v2';
import { ILocationState, useModule } from 'utils/hooks';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { useTabLocations } from './CreditNotes.hooks';
import { ILinesHandle } from './Lines/Lines.properties';
import { useCNContextSelector, useCNDispatch } from './CreditNotes.context';
import SummaryPanel from './SummaryPanel';
import CreditNotesLookupPanel from './CreditNotesLookupPanel';
import { ICreditNotesProps } from './CreditNotes.properties';
import { getTabs, MODULES } from './CreditNotes.constants';
import modules from './CreditNotes.modules';
import Lines from './Lines';

// Update it with Product Receipt when doing api integration
import styles from './CreditNotes.styles';
import { IValidatedResponse } from 'api/swaggerTypes';
import { CreditNotesActionTypes } from './CreditNotes.actions';
import { ProcessingProblemsDialogState } from 'components/common/ProcessingProblemsDialog/ProcessingProblemsDialog.properties';
import { ProcessingProblemsDialogDialogInitialState } from 'components/common/ProcessingProblemsDialog/ProcessingProblemsDialog.constants';
import { ICreditNotesLookupPanelHandle } from './CreditNotesLookupPanel/CreditNotesLookupPanel.properties';
import RestockingChargeDialog from './RestockingChargeDialog';
import LoadCreditNotesDialog from './LoadCreditNotesDialog';
import RecallCreditNoteDialog from './RecallCreditNoteDialog';
import ParkCreditNotes from './ParkCreditNotes';
import ProcessCreditNoteDialog from './ProcessCreditNoteDialog';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { cloneDeep } from 'lodash';
import { useApplyCreditNotesContext, useProcessConfirmation, useProcess, useDeleteCreditNotesLine, useCancelCreditNote, useParkCreditNotes, useRetrieveParkedCreditNotes, useFetchInvoiceLinesLookupPanel } from 'api/creditNotes/creditNotes';
import { isScreenRequest } from 'api/utils';
import { useQueryClient } from '@tanstack/react-query';
import { APIMethod, prepareUrl } from 'api/reactQuery/queryService';
import { APIRoutes } from 'api/creditNotes/constants';
import Notes from './Notes';
import { INotesHandle } from './Notes/Notes.properties';
import Details from './CreditNoteDetails';
import { ICreditNoteDetailsHandle } from './CreditNoteDetails/CreditNoteDetails.properties';
import LineDetails from './LineDetails/LineDetails.container';
import { ILineDetailsHandle } from './LineDetails/LineDetails.properties';
import PaymentEntryWrapper from 'components/common/PaymentEntryWrapper';
import Authorisation from 'components/common/Authorisation';

const CreditNotes = (props: ICreditNotesProps) => {
    const { classes, match, history, location, isV2, } = props;
    const params = new URLSearchParams(location.search);
    const paramaCreditNoteId = Number(params.get('CreditNote'));
    const CreditNoteId = useCNContextSelector<'CreditNote'>((state) => state.CreditNote);
    const CreditNoteContext = useCNContextSelector<'CreditNotesContext'>((state) => state.CreditNotesContext);
    const CreditNotesLookupPanelOpen = useCNContextSelector<'CreditNotesLookupPanelOpen'>((state) => state.CreditNotesLookupPanelOpen);
    const contextDispatch = useCNDispatch();
    const tabsData = useMemo(() => getTabs(CreditNoteId), [CreditNoteId]);
    const { actionBarProps } = useContext(ActionBarContext);
    const url = match ? match.url : '';
    const path = match ? match.path : '';
    const linesRef = useRef<ILinesHandle>();
    const notesRef = useRef<INotesHandle>();
    const detailsRef = useRef<ICreditNoteDetailsHandle>();
    const linesDetailsRef = useRef<ILineDetailsHandle>();
    const CreditNotesLookupPanelRef = useRef<ICreditNotesLookupPanelHandle>();
    const { tabLocations } = useTabLocations(url, location);
    const [loading, setLoading] = useState<boolean>(false);
    const [recallCreditNoteDialogOpen, setRecallCreditNoteDialogOpen] = useState<boolean>(false);
    const [loadCreditNotesDialogOpen, setLoadCreditNotesDialogOpen] = useState(false);
    const [loadingInvoiceLines, setLoadingInvoiceLines] = useState(false);
    const [invoiceLines, setInvoiceLines] = useState<any[]>([]);
    const [restockingChargeDialogOpen, setRestockingChargeDialogOpen] = useState<boolean>(false);
    const [parksaleConfirmationOpen, setParksaleConfirmationOpen] = useState<boolean>(false);
    const [processCreditNoteDialogOpen, setProcessCreditNoteDialogOpen] = useState<boolean>(false);
    const [processAuthorisationDialogOpen, setProcessAuthorisationDialogOpen] = React.useState(false);
    const [authorisationData, setAuthorisationData] = React.useState(null);
    const [mainContent, setMainContent] = useState({});
    const [processingProblemsDialog, setProcessingProblemsDialogDialog] = useState<ProcessingProblemsDialogState>(ProcessingProblemsDialogDialogInitialState);
    const { mutateAsync: applyWorksaleCriteriaMutateAsync } = useApplyCreditNotesContext();
    const useProcessConfirmationMutation = useProcessConfirmation();
    const useCancelCreditNoteMutation = useCancelCreditNote();
    const useProcessMutation = useProcess();
    const useParkCreditNotesMutation = useParkCreditNotes();
    const [creditNoteConfirmation, setCreditNoteConfirmation] = useState<any>({});
    const [paymentEntryOpen, setPaymentEntryOpen] = useState(false);
    const deleteLineMutation = useDeleteCreditNotesLine();
    const recallParkedCreditNotesMutation = useRetrieveParkedCreditNotes();
    const queryClient = useQueryClient();
    const { selectedTab, handleModuleChange } = useModule(
        history,
        location,
        'Lines',
        tabLocations,
        modules,
    );

    const { mutateAsync: fetchInvoiceLinesMutateAsync } = useFetchInvoiceLinesLookupPanel();

    const openLoadCreditNotesDialog = React.useCallback(
        async (workCreditNoteId: number) => {
            if (!workCreditNoteId) return;

            setLoadCreditNotesDialogOpen(true);
            setLoadingInvoiceLines(true);

            try {
                const response = await fetchInvoiceLinesMutateAsync({
                    WorkCreditNoteId: workCreditNoteId
                });

                setInvoiceLines(response?.CreditInvoiceLines || []);
            } finally {
                setLoadingInvoiceLines(false);
            }
        },
        [fetchInvoiceLinesMutateAsync]
    );

    async function loadParkedReceipts(): Promise<void> {
        try {
            setLoading(true);
            await recallParkedCreditNotesMutation.mutateAsync({ ParkedCreditNotesOnly: true });
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(
        () => {
            if (CreditNotesLookupPanelOpen) {
                loadParkedReceipts().catch((err) => { console.log(err); });
            }
        },
        [CreditNotesLookupPanelOpen]
    );

    React.useEffect(
        () => {
            contextDispatch({
                [CreditNotesActionTypes.setCreditNotesLookupPanelOpen]: !paramaCreditNoteId,
            });
        },
        [paramaCreditNoteId]
    );

    React.useEffect(
        () => {
            if (isV2 && selectedTab === 'CreditNoteDetails') handleModuleChange('Lines');
        },

        [isV2, selectedTab]

    );

    React.useEffect(
        () => {
            contextDispatch({
                [CreditNotesActionTypes.setCreditNote]: paramaCreditNoteId,
            });
            if (!paramaCreditNoteId) {
                handleModuleChange('Lines');
            }
        },
        []
    );

    React.useEffect(
        () => {
            if (CreditNoteId !== undefined) {
                mapPropertyToURL('CreditNote', CreditNoteId?.toString());
            } else {
                contextDispatch({
                    [CreditNotesActionTypes.setCreditNotesContext]: {},
                });
            }
        },
        [CreditNoteId]
    );

    const onContinueCallBack = React.useCallback(
        async (actionsCallback?: () => Promise<IValidatedResponse>) => {
            if (actionsCallback) {
                const response = await actionsCallback();
                validateProcessingProblems(response, actionsCallback);
            }
        },
        []
    );

    const closeProcessingProblemsDialogDialog = React.useCallback(
        async () => {
            setProcessingProblemsDialogDialog(ProcessingProblemsDialogDialogInitialState);
        },
        [setProcessingProblemsDialogDialog, processingProblemsDialog]
    );

    const validateProcessingProblems = React.useCallback(
        async (result, actionsCallback?: () => Promise<IValidatedResponse>) => {
            if (result?.Messages && result?.Messages.length > 0) {
                setProcessingProblemsDialogDialog(
                    {
                        open: true,
                        messages: result?.Messages,
                        description: 'The issues listed below have been identified during processing. Any errors found must be corrected before you can continue.\n Select BACK to return to the Fulfillment screen. Warning and Errors are highlighted in the fulfillment grid.',
                        onContinue: () => { onContinueCallBack(actionsCallback); },
                        onBack: () => {
                            closeProcessingProblemsDialogDialog();
                            // setReceiptConfirmationDialogOpen(false);
                            contextDispatch({
                                [CreditNotesActionTypes.setCreditNotesLookupPanelOpen]: false
                            });
                            linesRef.current.refreshDataGrid(result?.Messages);
                        }
                    });
            } else {
                closeProcessingProblemsDialogDialog();
            }
        },
        []
    );

    const mapPropertyToURL = (name: string, value: string) => {
        const currentUrl = window.location.href;
        const urlData = new URL(currentUrl);

        urlData.searchParams.set(name, value);
        window.history.replaceState({}, '', urlData);
    };

    const _handleModuleChange = React.useCallback(
        (tabToNavigate, inlineObject?: ILocationState, newSelectedTab: string | null = null) => {
            if (!CreditNoteId) return;
            if (newSelectedTab) {
                handleModuleChange(newSelectedTab, inlineObject);

                return;
            }
            switch (selectedTab) {
                case 'CreditNoteDetails':
                    if (detailsRef?.current?.isChangesSavedOrDiscarded()) {
                        handleModuleChange(tabToNavigate, inlineObject ?? { search: { CreditNote: CreditNoteId } });
                    }
                    break;
                case 'LineDetails':
                    if (linesDetailsRef?.current?.isChangesSavedOrDiscarded()) {
                        linesDetailsRef?.current?.LineDetailsCancel();
                    }
                    break;
                case 'KitComponents':
                    const param = new URLSearchParams(location.search);
                    const LineNumber = param.get('LineNumber');
                    const WorkCreditNoteLineId = param.get('WorkCreditNoteLineId');

                    if (tabToNavigate === 'Lines') {
                        handleModuleChange(tabToNavigate, inlineObject ?? { search: { CreditNote: CreditNoteId } });
                    } else {
                        handleModuleChange(tabToNavigate, inlineObject ?? { search: { CreditNote: CreditNoteId, LineNumber, WorkCreditNoteLineId } });
                        linesDetailsRef.current?.reload();
                    }
                    break;
                case 'ComponentDetails':
                    const componentParam = new URLSearchParams(location.search);
                    const componentLineNumber = componentParam.get('LineNumber');
                    const componentWorkCreditNoteLineId = componentParam.get('WorkCreditNoteLineId');

                    if (tabToNavigate === 'Lines') {
                        handleModuleChange(tabToNavigate, inlineObject ?? { search: { CreditNote: CreditNoteId } });
                    } else {
                        handleModuleChange(tabToNavigate, inlineObject ?? { search: { CreditNote: CreditNoteId, LineNumber: componentLineNumber, WorkCreditNoteLineId: componentWorkCreditNoteLineId } });
                    }
                    break;
                case 'Notes':
                    if (notesRef?.current?.isChangesSavedOrDiscarded()) {
                        handleModuleChange(tabToNavigate, inlineObject ?? { search: { CreditNote: CreditNoteId } });
                    }
                    break;
                default:
                    handleModuleChange(tabToNavigate, inlineObject ?? { search: { CreditNote: CreditNoteId } });
            }
        },
        [selectedTab, CreditNoteContext, handleModuleChange, CreditNoteId]
    );

    const cancelCreditNote = async () => {
        const response = await useCancelCreditNoteMutation.mutateAsync({ WorkCreditNoteId: CreditNoteId });
        if (response?.Status) {
            clearCreditNoteContext();
            await loadParkedReceipts();
        }
    };

    const clearCreditNoteContext = () => {
        contextDispatch(
            {
                [CreditNotesActionTypes.setCreditNote]: 0,
                [CreditNotesActionTypes.setCreditNotesContext]: {}
            });
        linesRef.current.fastEntryResetToDefault();

        contextDispatch({
            [CreditNotesActionTypes.setCreditNotesLookupPanelOpen]: true
        });
    };

    const handleActionBarButtonClick = React.useCallback(
        async (action: string) => {
            switch (action) {
                case 'Back':
                    switch (selectedTab) {
                        case 'Details':
                            _handleModuleChange('Lines');
                            break;
                        case 'ComponentDetails':
                            linesDetailsRef.current.componentDetailOnCancel();
                            break;
                        case 'KitComponents':
                            const kitComponentsParam = new URLSearchParams(location.search);
                            const kitComponentsLineNumber = kitComponentsParam.get('LineNumber');
                            const kitComponentsWorkCreditNoteLineId = kitComponentsParam.get('WorkCreditNoteLineId');
                            _handleModuleChange('LineDetails', {
                                search: {
                                    CreditNote: CreditNoteId,
                                    LineNumber: kitComponentsLineNumber || '',
                                    WorkCreditNoteLineId: kitComponentsWorkCreditNoteLineId || ''
                                }
                            });
                            break;
                        default:
                    }
                    break;
                case 'Process':
                    initializeProcessCreditNote();
                    break;
                case 'RecallCreditNote':
                    setRecallCreditNoteDialogOpen(true);
                    break;
                case 'Load':
                    if (!CreditNoteId) return;

                    openLoadCreditNotesDialog(CreditNoteId);
                    break;
                case 'ParkCreditNote':
                    setParksaleConfirmationOpen(true);
                    break;
                case 'CreditNoteDetailsOk':
                case 'CreditNoteDetailsCancel':
                    detailsRef?.current?.[action]();
                    break;

                case 'NotesOk':
                case 'NotesCancel': {
                    // freightChargeDetailsRef.current?.[action]();
                    break;
                }

                case 'CancelCreditNote':
                    cancelCreditNote();
                    break;
                case 'LineDetailsOk':
                case 'LineDetailsCancel':
                case 'LineDetailsPicking':
                    linesDetailsRef.current?.[action]();
                    break;
                case 'KitComponents':
                    linesDetailsRef.current.components();
                    break;
                case 'ComponentWarehouseSelection':
                    linesDetailsRef.current.componentDetailsWarehouse();
                    break;
                case 'ComponentDetailsLotSerial':
                    linesDetailsRef.current.componentDetailsLotSerial();
                    break;
                case 'ComponentDetailsOk':
                    linesDetailsRef.current.componentDetailOnOk();
                    break;
                case 'ComponentDetailsCancel':
                    linesDetailsRef.current.componentDetailOnCancel();
                    break;
                default:
            }
        },
        [selectedTab, _handleModuleChange, linesRef]
    );

    async function handleApplyCreditNoteContext(criteria: any): Promise<any> {
        try {
            setLoading(true);

            const updatedCriteria = cloneDeep(criteria);
            const response = await applyWorksaleCriteriaMutateAsync({ ...updatedCriteria, WorkCreditNoteId: updatedCriteria.CreditNote !== 0 && updatedCriteria.CreditNote ? updatedCriteria.CreditNote : (CreditNoteId || 0) });
            if (response?.Status && response?.CreditNoteContext) {
                showSnackBar({ variant: 'success', message: 'Credit note changes applied successfully.' });
                contextDispatch({
                    [CreditNotesActionTypes.setCreditNote]: response?.WorkCreditNoteId,
                });
                contextDispatch({
                    [CreditNotesActionTypes.setCreditNotesContext]: response.CreditNotesContext?.inlineObject,
                });
                if (isScreenRequest(response?.Forms, 'LoadInvoiceLinesScreenRequest')) {
                    openLoadCreditNotesDialog(response.WorkCreditNoteId);
                }
            }

            return response;
        } finally {
            setLoading(false);
        }
    }

    // const clearCreditNoteContext = () => {
    //     contextDispatch(
    //         {
    //             [CreditNotesActionTypes.setCreditNote]: 0,
    //             [CreditNotesActionTypes.setCreditNoteContext]: {}
    //         });
    //     linesRef.current.fastEntryResetToDefault();

    //     contextDispatch({
    //         [CreditNotesActionTypes.setCreditNotesLookupPanelOpen]: true
    //     });
    // };

    const postProcessingCreditNotes = async () => {
        showSnackBar({ variant: 'success', message: 'Credit Note processed successfully.' });
        setProcessCreditNoteDialogOpen(false);
        setProcessAuthorisationDialogOpen(false);
        clearCreditNoteContext();
        await loadParkedReceipts();
    };

    const initializeProcessCreditNote = React.useCallback(
        async (data?: any) => {
            const response = await useProcessConfirmationMutation.mutateAsync({ WorkCreditNoteId: CreditNoteId, ...data });
            setCreditNoteConfirmation(response?.CreditNoteConfirmation);
            if (response?.Status && isScreenRequest(response?.Forms, 'RestockChargeEntry')) {
                setRestockingChargeDialogOpen(true);

                return;
            } else if (response?.Status) {
                setProcessCreditNoteDialogOpen(true);
            }
        },
        [CreditNoteId]
    );
    const reloadLinesAndSummary = React.useCallback(
        () => {
            linesRef.current?.refreshDataGrid();
            // Invalidate and refetch summary query
            if (CreditNoteId) {
                const summaryUrl = prepareUrl(APIRoutes.creditNotes.summary.retrieve, { WorkCreditNoteId: CreditNoteId }, null, true);
                queryClient.invalidateQueries({
                    queryKey: [APIMethod.GET, summaryUrl]
                });
            }
        },
        [linesRef, CreditNoteId, queryClient]);

    const authoriseCreditNote = React.useCallback(
        async (data?: any) => {
            const { AuthUserId, AuthUserPin, ...rest } = data;

            const payload = {
                WorkCreditNoteId: CreditNoteId,
                ...rest,
                AuthUserID: AuthUserId,
                AuthUserPIN: AuthUserPin
            };

            const response = await useProcessMutation.mutateAsync(payload);
            if (response?.Status && response?.Forms?.[0]?.MessageType === 'Success') postProcessingCreditNotes();
            if (!response?.Status && isScreenRequest(response?.Forms, 'AuthorisationScreenRequest')) {
                setAuthorisationData((response?.CreditNoteConfirmation as any)?.inlineObject);
            }
            reloadLinesAndSummary();
        },
        [CreditNoteId, reloadLinesAndSummary]
    );

    const processCreditNotes = async (data) => {
        const payload = {
            ...data,
            WorkCreditNoteId: CreditNoteId,
        };
        const response = await useProcessMutation.mutateAsync({
            WorkCreditNoteId: CreditNoteId,
            ...data,
        });
        setMainContent(payload);

        if (isScreenRequest(response?.Forms, 'PaymentEntry')) {
            setPaymentEntryOpen(true);
            setCreditNoteConfirmation(response?.CreditNoteConfirmation);
            setProcessCreditNoteDialogOpen(false);

            return;
        }

        if (isScreenRequest(response?.Forms, 'AuthorisationScreenRequest')) {
            setProcessCreditNoteDialogOpen(false);
            setAuthorisationData((response?.CreditNoteConfirmation as any)?.inlineObject);
            setProcessAuthorisationDialogOpen(true);

            return;
        }

        if (response?.Status && !isScreenRequest(response?.Forms)) {
            postProcessingCreditNotes();
        }
    };

    const restockingHandler = (data) => {
        setRestockingChargeDialogOpen(false);
        initializeProcessCreditNote(
            {
                RestockingCharge: {
                    ...data,
                }
            });
    };

    async function handleApplyPaymentEntry(data: any): Promise<void> {
        const payload = {
            ...creditNoteConfirmation?.inlineObject,
            PaymentDetail: {
                ...data?.ProcessPaymentDetail,
            },
        };

        processCreditNotes(payload);
        setPaymentEntryOpen(false);
    }

    const paymentEntryOnCancel = React.useCallback(
        async () => {
            const lineId = creditNoteConfirmation?.inlineObject?.RestockingChargeLineId;
            if (lineId !== 0) {
                await deleteLineMutation.mutateAsync({
                    WorkCreditNoteLineId: creditNoteConfirmation?.inlineObject?.RestockingChargeLineId,
                });
            }
            setPaymentEntryOpen(false);
        },
        [deleteLineMutation, creditNoteConfirmation]
    );

    async function handleOkParkCreditNotes(query: any): Promise<void> {
        try {
            const response = await useParkCreditNotesMutation.mutateAsync({
                WorkCreditNoteId: CreditNoteId,
                Note: query?.InternalNotes || ''
            });

            if (response?.Status) {
                setParksaleConfirmationOpen(false);
                clearCreditNoteContext();
                await loadParkedReceipts();
            }

        } catch (err) {
            showSnackBar({ variant: 'error', message: `Unexpected Error ${err.message}.` });
        }
    }

    return (
        <div className={classes.mainDiv}>
            <div className={classes.innerContainer}>
                <CreditNotesLookupPanel
                    ref={CreditNotesLookupPanelRef}
                    open={CreditNotesLookupPanelOpen}
                    loading={loading}
                    parkedCreditNotesItems={recallParkedCreditNotesMutation.data?.ParkedCreditNotes}
                    parkedCreditNotesItemsLoading={recallParkedCreditNotesMutation?.isLoading}
                    onOpenChanged={(open) => {
                        contextDispatch({
                            [CreditNotesActionTypes.setCreditNotesLookupPanelOpen]: open,
                        });
                    }}
                    onApplyCreditNotesContext={handleApplyCreditNoteContext}
                />
                <div
                    className={classNames(classes.PSSection, {
                        [classes.PSSectionMarginOpen]: CreditNotesLookupPanelOpen,
                        [classes.PSSectionMarginClose]: !CreditNotesLookupPanelOpen
                    })}
                >
                    <BreadcrumbBar
                        mainModule={'Credit Notes'}
                        activeModuleId={selectedTab}
                        moduleTree={modules}
                        onBreadcrumbClick={_handleModuleChange}
                        onMainModuleClick={() => _handleModuleChange('Lines')}
                        hideOptionsMenu={true}
                    />
                    <SummaryPanel />
                    {tabsData.some(({ id }) => id === selectedTab) && <NavigationTabs
                        lookupOpen={CreditNotesLookupPanelOpen}
                        tabsData={tabsData}
                        activeTabId={selectedTab}
                        onTabChange={_handleModuleChange}
                    />}
                    <Route
                        key={'Lines'}
                        path={path}
                        exact
                        render={() => <Lines forwardRef={linesRef} changeSelectedTab={_handleModuleChange} />}
                    />
                    <Route
                        key={'Notes'}
                        path={`${path.endsWith('/') ? path : `${path}/`}notes`}
                        exact
                        render={() => <Notes innerRef={notesRef} changeSelectedTab={_handleModuleChange} />}
                    />
                    <Route
                        key={'CreditNoteDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}credit-note-details`}
                        exact
                        render={() => <Details innerRef={detailsRef} changeSelectedTab={_handleModuleChange} />}
                    />
                    <Route
                        key={'LinesDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}line-details*`}
                        exact
                        render={(routeProps) => <LineDetails {...routeProps} innerRef={linesDetailsRef} changeSelectedTab={_handleModuleChange} onUpdateSuccess={() => { linesRef.current?.refreshDataGrid(); }} navigateBack={() => { handleModuleChange('Lines'); }} />}
                    />

                </div>
            </div>
            <ActionBar {...actionBarProps} onActionClick={handleActionBarButtonClick} />
            {
                recallCreditNoteDialogOpen ?
                    <RecallCreditNoteDialog
                        open={recallCreditNoteDialogOpen}
                        onRecall={() => {
                            setRecallCreditNoteDialogOpen(false);
                            contextDispatch({
                                [CreditNotesActionTypes.setCreditNotesLookupPanelOpen]: false,
                            });
                        }}
                        onCancel={() => setRecallCreditNoteDialogOpen(false)}
                    /> : undefined
            }
            <LoadCreditNotesDialog
                open={loadCreditNotesDialogOpen}
                dialogStyles={{
                    width: '1000px',
                    minHeight: 500,
                    maxHeight: 500,
                    height: '100%'
                }}
                invoiceLines={invoiceLines}
                loading={loadingInvoiceLines}
                onClose={() => setLoadCreditNotesDialogOpen(false)}
                onOk={() => linesRef.current.refreshDataGrid()}
            />
            {
                parksaleConfirmationOpen ?
                    <ParkCreditNotes
                        open={parksaleConfirmationOpen}
                        onCancel={() => { setParksaleConfirmationOpen(false); }}
                        onOk={handleOkParkCreditNotes}
                    /> : undefined
            }
            {
                processCreditNoteDialogOpen ?
                    <ProcessCreditNoteDialog
                        open={processCreditNoteDialogOpen}
                        onCancel={() => { setProcessCreditNoteDialogOpen(false); }}
                        initialValues={creditNoteConfirmation}
                        isProcessing={false}
                        onOk={processCreditNotes}
                    /> : undefined
            }

            {
                restockingChargeDialogOpen ?
                    <RestockingChargeDialog
                        open={restockingChargeDialogOpen}
                        loading={false}
                        data={creditNoteConfirmation}
                        onOk={restockingHandler}
                        onCancel={() => {
                            setRestockingChargeDialogOpen(false);
                        }}
                    /> : undefined
            }

            <PaymentEntryWrapper
                module={MODULES.CREDIT_NOTES}
                initialValues={creditNoteConfirmation?.inlineObject?.PaymentInformation}
                open={paymentEntryOpen}
                onCancel={paymentEntryOnCancel}
                onApply={handleApplyPaymentEntry}
            />
            <Authorisation
                open={processAuthorisationDialogOpen}
                mainContent={mainContent}
                initialValues={authorisationData}
                valuesSchema={{}}
                onOk={authoriseCreditNote}
                onClose={() => {
                    setProcessAuthorisationDialogOpen(false);
                    clearCreditNoteContext();
                }}
            />
        </div >
    );
};

export default withRouter(withStyles(styles, { index: 1 })(CreditNotes));
